/**
 * One camera, held for as long as the user is scanning — not one per mount.
 *
 * `ScanPane` used to call `getUserMedia` in a mount effect and stop the tracks
 * in the cleanup. That reads well and behaves badly, because scan is a *mode*
 * of the add sheet rather than a route: going back to the list to check
 * something and returning to the camera unmounts and remounts the pane, so a
 * single logging session asked the browser for the camera three or four times.
 *
 * Each of those asks is a chance to be asked back. A permission that was
 * granted "just this once" — Chrome's own middle option, and the default on a
 * browser set to ask — is spent by the first stream, so the second acquisition
 * prompts again. The user is holding a packet up to a phone and being asked to
 * agree to the camera between every item.
 *
 * So the stream outlives the pane. Acquisition is refcounted and memoised: the
 * pane takes a reference on mount and drops it on unmount, and a drop to zero
 * does not stop anything. What stops it is `closeCamera`, which the sheet calls
 * when it closes — the moment scanning is actually over.
 *
 * **The indicator light still has to mean something.** Holding a stream the
 * user cannot see is the thing a camera indicator exists to expose, so the hold
 * is bounded at both ends: `closeCamera` on the way out of the sheet, and
 * `LINGER_MS` for a sheet left open on the table. Neither is a heuristic about
 * intent — one is the user leaving, the other is the user gone.
 *
 * Failure is a value here rather than a thrown string, because the four ways
 * this fails want four different sentences and only one of them is worth a
 * retry button. `NotAllowedError` in particular used to be reported as "No
 * camera available on this device", which is both false and a dead end: it
 * tells somebody who denied a prompt that their hardware is missing.
 *
 * **Which camera is the user's to say.** `facingMode: "environment"` is a good
 * default and a poor guarantee: a phone with three rear lenses hands back
 * whichever one the browser likes — often the ultra-wide, which reads a barcode
 * from further away than anybody holds a packet — and a laptop with a document
 * camera clipped to it has an opinion the browser cannot know. So the default
 * stands and a deliberate choice overrides it, remembered on the device that
 * made it: picking your camera once should not be a thing you do every meal.
 *
 * **A choice that cannot be honoured is answered, not swallowed.** The picked
 * camera can be busy, or gone, or belong to a laptop this browser profile has
 * since left. Falling back to another one is right — a viewfinder beats an
 * error — but doing it silently means the user is looking at the wrong picture
 * with no idea why, and reaches for the picker that is already set to the
 * camera they are not getting. So the substitution rides along on the feed
 * (`CameraFeed.substituted`) and the screen says it in words.
 *
 * That notice is only raised for a camera we know is *here*: see `locate`. A
 * remembered id that matches nothing on this machine is not a failure worth
 * reporting — it is last week's laptop, and the honest response is the default
 * camera with nothing said about it.
 */

/**
 * How long the camera survives with nobody holding it, in ms.
 *
 * Long enough that leaving scan to check a weight and coming back is free —
 * which is the whole point — and short enough that a sheet abandoned mid-scan
 * puts the light out on its own. Ninety seconds is well past any round trip
 * through the list and well short of "why is my camera on".
 */
const LINGER_MS = 90_000;

/** Where a deliberate choice of camera rests between visits. */
const PICK_KEY = "calorio:camera";

/** Why the camera did not open, at the granularity the screen answers. */
export type CameraProblem =
  /** The user said no, or dismissed the prompt. Asking again may still work. */
  | "denied"
  /** The user said no and the browser has stopped relaying our asks. */
  | "blocked"
  /** No camera on the device, or none that matches. */
  | "missing"
  /** There is one and something else has it — another tab, or another app. */
  | "busy"
  /** Anything else, including a page that is not on a secure origin. */
  | "failed";

export class CameraDenied extends Error {
  constructor(readonly problem: CameraProblem) {
    super(problem);
    this.name = "CameraDenied";
  }
}

export const CAMERA_COPY: Record<CameraProblem, string> = {
  denied: "Calorio needs the camera to read a barcode or a label.",
  blocked:
    "The camera is blocked for this site. Allow it in your browser's address bar, then try again.",
  missing: "No camera available on this device.",
  busy: "Something else is using the camera. Close it and try again.",
  failed: "Couldn't start the camera.",
};

/** A camera the user could pick, as the picker needs to name it. */
export interface CameraOption {
  /** `deviceId` — what re-opening this exact camera takes. */
  id: string;
  /** The browser's own name for it, or a numbered stand-in for one with none. */
  label: string;
}

/** The live feed, and whether it is the camera that was asked for. */
export interface CameraFeed {
  stream: MediaStream;
  /** Which camera is actually running. Empty where the browser will not say. */
  deviceId: string;
  /**
   * The camera that was asked for, when the running one is not it.
   *
   * Null on the ordinary path, including the ordinary path where nobody has
   * ever picked a camera. A value here is a sentence the screen owes the user.
   */
  substituted: CameraOption | null;
}

/** The live stream, if there is one. Shared by every holder. */
let held: MediaStream | null = null;
/** How many callers currently want it. Zero starts the linger, not a stop. */
let holders = 0;
let linger: number | undefined;
/** The substitution that produced `held`, so every holder is told about it. */
let substitute: CameraOption | null = null;

/**
 * The camera to open, when the user has said which.
 *
 * `confirmed` is the difference between a choice made in front of a list of
 * this machine's cameras and an id restored from storage that may name nothing
 * here at all. Only a confirmed pick is worth a notice when it fails —
 * see the header, and `locate`, which is what promotes one to the other.
 */
let pick: { id: string; label: string; confirmed: boolean } | null = restorePick();

/** Rear camera: the product is in front of you, not behind the screen. */
const REAR: MediaStreamConstraints = { video: { facingMode: "environment" } };

function restorePick() {
  try {
    const raw = localStorage.getItem(PICK_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { id?: unknown; label?: unknown };
    if (typeof saved.id !== "string" || !saved.id) return null;
    return {
      id: saved.id,
      label: typeof saved.label === "string" ? saved.label : "",
      confirmed: false,
    };
  } catch {
    // Malformed, or Safari with site data blocked, where reading throws. The
    // default camera is a fine place to be; it is where everybody starts.
    return null;
  }
}

function rememberPick(next: { id: string; label: string }) {
  try {
    localStorage.setItem(PICK_KEY, JSON.stringify({ id: next.id, label: next.label }));
  } catch {
    // It holds for this session; it just does not survive leaving.
  }
}

function classify(error: unknown): CameraProblem {
  const name = error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "denied";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "missing";
  if (name === "NotReadableError" || name === "AbortError") return "busy";
  return "failed";
}

/**
 * Has the user already refused, permanently enough that asking is pointless?
 *
 * The Permissions API is the only way to tell "denied once" from "denied and
 * remembered", and it is the difference between offering a retry button and
 * explaining where the address-bar control is. Absent on WebKit, which is why
 * this answers `null` rather than false — an unknown is not a no, and a browser
 * that cannot say gets the retry, which is the harmless of the two mistakes.
 */
export async function cameraBlocked(): Promise<boolean | null> {
  try {
    const status = await navigator.permissions?.query({
      name: "camera" as PermissionName,
    });
    return status ? status.state === "denied" : null;
  } catch {
    return null;
  }
}

/**
 * Every camera on this device, named well enough to choose between.
 *
 * Labels are a permission-gated secret: before the user has agreed to a camera
 * `enumerateDevices` answers with the right *number* of cameras and blank names
 * for all of them, which is why the picker is drawn from this only once a feed
 * is running. The numbered stand-in is for the browsers that withhold names
 * even then — a list of three identical blanks is not a choice.
 */
export async function listCameras(): Promise<CameraOption[]> {
  try {
    const devices = (await navigator.mediaDevices?.enumerateDevices()) ?? [];
    return devices
      .filter((device) => device.kind === "videoinput" && device.deviceId)
      .map((device, index) => ({
        id: device.deviceId,
        label: device.label || `Camera ${index + 1}`,
      }));
  } catch {
    return [];
  }
}

/**
 * Work out whether the remembered camera is one of *this* machine's.
 *
 * Two things hang off the answer. A pick that is here can be opened by the id
 * the browser is using for it right now — which is not always the id it was
 * saved under, since WebKit rotates device ids between sessions, so a saved
 * name is the more durable handle of the two. And a pick that is here is worth
 * a sentence when it will not open, where one that is not here is somebody
 * else's laptop and gets the default camera in silence.
 *
 * A list with no names in it settles nothing: that is a browser that has not
 * granted the camera yet, so the remembered id is tried on its own merits.
 */
async function locate() {
  const want = pick;
  if (!want || want.confirmed) return;

  const options = await listCameras();
  if (!options.length || !options.some((option) => option.label)) return;

  const here =
    options.find((option) => option.id === want.id) ??
    (want.label ? options.find((option) => option.label === want.label) : undefined);
  pick = here ? { id: here.id, label: here.label, confirmed: true } : null;
}

/**
 * Acquisitions run one at a time, in the order they were asked for.
 *
 * Two panes mounting at once must make one ask — that is the memoisation the
 * repeat-prompt fix rests on — and a switch arriving while an acquisition is in
 * flight must not race the stream it is about to replace. A queue answers both:
 * the second caller finds `held` already there, and the switch stops a stream
 * that has finished arriving rather than one that is halfway.
 */
let queue: Promise<unknown> = Promise.resolve();

function serial<T>(job: () => Promise<T>): Promise<T> {
  const run = queue.then(job, job);
  queue = run.then(
    () => {},
    () => {},
  );
  return run;
}

function activeId(media: MediaStream): string {
  const track = media.getVideoTracks()[0];
  return track?.getSettings?.().deviceId ?? "";
}

function feedOf(media: MediaStream): CameraFeed {
  return { stream: media, deviceId: activeId(media), substituted: substitute };
}

async function open(id: string | null): Promise<MediaStream> {
  // `exact`, so a picked camera that cannot be had fails loudly here and is
  // answered by the fallback below. A soft `deviceId` would quietly hand back
  // a different camera with nothing to distinguish it from success.
  const media = await navigator.mediaDevices?.getUserMedia(
    id ? { video: { deviceId: { exact: id } } } : REAR,
  );
  if (!media) throw new Error("no mediaDevices");
  return media;
}

async function acquire(): Promise<CameraFeed> {
  await locate();
  substitute = null;
  let media: MediaStream | null = null;

  try {
    const want = pick;
    if (want) {
      try {
        media = await open(want.id);
      } catch (error) {
        // A refusal is about the site, not about the device. Trying a second
        // camera would earn a second refusal and then report "your front
        // camera wasn't available" for a permission the user declined.
        if (classify(error) === "denied") throw error;
        if (want.confirmed) substitute = { id: want.id, label: want.label };
      }
    }
    media ??= await open(null);
  } catch (error) {
    const problem = classify(error);
    throw new CameraDenied(
      problem === "denied" && (await cameraBlocked()) === true ? "blocked" : problem,
    );
  }

  // Nobody wanted it by the time it arrived — the sheet closed while the prompt
  // was up. Do not leave a stream running that no pane will ever attach to, and
  // do not cache it as the held one.
  if (holders === 0) {
    media.getTracks().forEach((track) => track.stop());
    substitute = null;
    throw new CameraDenied("failed");
  }

  held = media;
  return feedOf(media);
}

/**
 * Take a reference on the camera, starting it if it is not already running.
 *
 * Resolves with the same `MediaStream` every holder gets, and with the same
 * account of which camera it is — a substitution is a fact about the running
 * feed, so the second pane to attach to it is told as well as the first.
 * Rejects with `CameraDenied` carrying which of the failures it was — see
 * `CAMERA_COPY`.
 */
export async function holdCamera(): Promise<CameraFeed> {
  holders += 1;
  window.clearTimeout(linger);
  linger = undefined;

  return serial(async () => {
    // A stream whose track has ended — the device was unplugged, or the OS took
    // it back — is not reusable, and handing it out produces a black rectangle
    // rather than an error. Drop it and ask again.
    if (held && !held.getVideoTracks().some((track) => track.readyState === "live")) {
      stop();
    }
    if (held) return feedOf(held);

    // Deliberately no decrement on the failure path. Every `holdCamera` is
    // paired with exactly one `dropCamera` by the caller's own cleanup,
    // including the ones that failed, and a decrement here would release a
    // reference somebody else took while this ask was still in flight.
    return acquire();
  });
}

/**
 * Open a different camera, and remember that this is the one.
 *
 * The refcount is untouched: the holders are holding *the camera*, not a
 * particular stream, and the pane that asked for this is still mounted. The old
 * stream is stopped first, so a phone does not have two lenses lit at once and
 * the second `getUserMedia` is not competing with the stream it replaces.
 */
export async function switchCamera(camera: CameraOption): Promise<CameraFeed> {
  pick = { ...camera, confirmed: true };
  rememberPick(camera);

  return serial(async () => {
    stop();
    return acquire();
  });
}

function stop() {
  held?.getTracks().forEach((track) => track.stop());
  held = null;
  substitute = null;
}

/**
 * Let go, without putting the camera out.
 *
 * The pane calls this on unmount, and the whole design is that it usually does
 * nothing: leaving scan for the list is a step *within* scanning, and treating
 * it as the end of it is what caused the repeat prompts. `LINGER_MS` is the
 * backstop for the case where it really was the end and nobody said so.
 */
export function dropCamera() {
  holders = Math.max(0, holders - 1);
  if (holders > 0 || !held) return;
  window.clearTimeout(linger);
  linger = window.setTimeout(() => {
    if (holders === 0) stop();
  }, LINGER_MS);
}

/**
 * Scanning is over. Put the light out now.
 *
 * Called by whatever owns the session the camera belongs to — the add sheet
 * closing, the scan page unmounting. This is the guarantee the indicator makes
 * to the user, so it ignores the refcount rather than negotiating with it.
 */
export function closeCamera() {
  holders = 0;
  window.clearTimeout(linger);
  linger = undefined;
  stop();
}
